import { type SmartCreationDraft, SmartCreationDraftSchema } from "@adpilot/shared";
import { z } from "zod";

import { smartCreationOwnerKey } from "@/lib/smart-creation-draft-store";

const CACHE_VERSION = 1;
const SmartCreationLocalStateSchema = z
  .object({
    version: z.literal(CACHE_VERSION),
    revision: z.number().int().positive(),
    draft: SmartCreationDraftSchema,
  })
  .strict();

function storageKey(shopId: string, planId: string): string {
  return `adpilot:smart-creation:${smartCreationOwnerKey()}:${shopId}:${planId}`;
}

export function readSmartCreationLocalState(
  shopId: string,
  planId: string,
  revision: number,
): SmartCreationDraft | null {
  if (typeof window === "undefined") return null;
  const key = storageKey(shopId, planId);
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = SmartCreationLocalStateSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.revision !== revision) {
      window.localStorage.removeItem(key);
      return null;
    }
    return parsed.data.draft;
  } catch {
    clearSmartCreationLocalState(shopId, planId);
    return null;
  }
}

export function writeSmartCreationLocalState(
  shopId: string,
  planId: string,
  revision: number,
  draft: SmartCreationDraft,
): boolean {
  if (typeof window === "undefined") return false;
  try {
    window.localStorage.setItem(
      storageKey(shopId, planId),
      JSON.stringify({ version: CACHE_VERSION, revision, draft }),
    );
    return true;
  } catch {
    // Browser storage is a best-effort continuity layer. The current in-memory
    // edit remains authoritative until the user leaves or saves the step.
    return false;
  }
}

export function clearSmartCreationLocalState(shopId: string, planId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey(shopId, planId));
    window.localStorage.removeItem(`adpilot:smart-creation:${shopId}:${planId}`);
  } catch {
    // Clearing an unavailable browser store has no effect on server state.
  }
}
